import { toJpeg } from 'html-to-image';

/**
 * 处理深拷贝后的 dom 元素, 用于生成分享图
 *
 * @param cloned 深拷贝后的 dom 元素
 * @param msgIds 需要保留的消息卡片 id
 * @returns 处理后的 dom 元素
 */
const processForShare = (cloned: HTMLElement, msgIds: string[]) => {
  cloned.classList.add('open');

  /* 隐藏不包含在 msgIds 内的消息卡片 */
  const cardItems = cloned.querySelectorAll<HTMLElement>('.chat-card-item-wrapper');
  cardItems.forEach((item) => {
    const id = item.getAttribute('data-id') ?? '';
    if (!msgIds.includes(id)) {
      item.remove();
    }
  });

  /* 搜索源列表需要删掉 */
  const searchInfoLists = cloned.querySelectorAll<HTMLElement>('.search-info-list');
  searchInfoLists.forEach((list) => {
    list.remove();
  });
  const textElements = cloned.querySelectorAll('strong');
  textElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.lineHeight = '1.4'; // 增加行高以减少重叠
      el.style.overflow = 'visible'; // 确保文字可见
      el.style.whiteSpace = 'normal'; // 改为normal以更好控制换行
      el.style.maxHeight = 'none'; // 移除最大高度限制
      el.style.position = 'relative'; // 确保定位正常
      el.style.margin = '0.5em 0'; // 增加垂直间距
      el.style.textOverflow = 'clip'; // 防止省略号截断
      el.style.wordBreak = 'break-word'; // 确保长词可以换行
      el.style.display = 'inline-block'; // 使用inline-block控制布局
      el.style.width = '100%'; // 设置宽度以确保正确换行
      el.style.fontWeight = '700'; // 确保加粗效果一致
      // 添加字间距，改善文字拥挤情况
      el.style.letterSpacing = '0.01em';
    }
  });

  return cloned;
};

// 添加一个处理跨域图片的函数
const handleCrossOriginImages = (el: HTMLElement) => {
  const images = Array.from(el.querySelectorAll('img'));

  images.forEach((img) => {
    const src = img.getAttribute('src') || '';

    // 判断是否为跨域图片
    if (!src.startsWith('data:') && !src.startsWith('blob:')) {
      try {
        const url = new URL(src, window.location.href);
        if (url.origin !== window.location.origin) {
          console.log(`检测到跨域图片: ${src}`);

          // 替换为base64占位图
          img.src =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAABmJLR0QA/wD/AP+gvaeTAAAAB3RJTUUH5AofEiYTGNQJfwAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAABZElEQVR42u3dsU0DMRgFYBcZgQ0yQmrECIyQDViAHdggNWIERkhNR7vAkhPSXZGv+J6EpP9e4/IXOnAcAAAAAAAAAPyF53U9rtv7cTyty/6Kbq/r+bmut3V9reu/Pe92u91ut9vtdrvd9pv/nWWMy7K+PsZxPl/OZ0nS3UP36Tw1ld81hNGHMYZxGUYfxhjGZRh9GGMYl2H0YYxhXIbRhzGGcRlGH8YYxmUYfRhjGJdh9GGMYVyGcRDGZRi/w7hbHmeZ9jDGMC7DyMO4htEZxhjGZRh5GNcwOjPGMC7DyMO4htGZMYZxGUYexjWMzozvw8jDuIbRmfF9GHkY1zA6M74PIw/jGkZnxvdh5GFcw4gZszM6j6/9qrkw4jDyMOLDyA8jDiM/jDiM/DDiMPLDiMPIDyMOIz+MOIz8MOIw8sOIw8gPIw4jP4w4jPww4jDyw4jDyA8jDiM/jDiM/DDiMAAAAAAAAAAAAD36ASGSzRqdQlv3AAAAAElFTkSuQmCC';

          // 或者可以完全移除图片
          // if (img.parentNode) {
          //   img.parentNode.removeChild(img);
          // }
        }
      } catch (e) {
        // URL解析错误，忽略
      }
    }
  });

  return el;
};

const preLoadImagesFromElement = (el: HTMLElement) => {
  return new Promise((resolve) => {
    const handle = setTimeout(() => resolve(false), 5000);
    let count = 0;
    el.querySelectorAll('img').forEach(() => {
      count += 1;
    });
    el.querySelectorAll('img').forEach((img) => {
      const url = img.getAttribute('src');
      if (url) {
        const img = new Image();
        img.src = url;
        img.onload = () => {
          count -= 1;
          if (count === 0) {
            clearTimeout(handle);
            resolve(true);
          }
        };
      }
    });
  });
};

const elementToImg = async (el: HTMLElement) => {
  const rect = el?.getBoundingClientRect();
  try {
    const dataUrl = await toJpeg(el, {
      cacheBust: true,
      quality: 0.95, //
      pixelRatio: window.devicePixelRatio || 1, // 使用设备像素比
      canvasHeight: rect.height, // 不再翻倍
      canvasWidth: rect.width, // 不再翻倍
      skipFonts: true, // 跳过字体处理，使用系统默认字体
    });
    return dataUrl;
  } catch (error) {
    console.error(error);
    return '';
  }
};

/**
 * 将URL转换为Base64编码的图片
 *
 * @param url 图片URL
 * @param timeout 超时时间(毫秒)，默认5000ms
 * @returns Promise<string> 返回Base64编码的图片数据
 */
const convertUrlToBase64 = async (url: string, timeout = 5000): Promise<string> => {
  return new Promise((resolve, reject) => {
    // 设置超时处理
    const timeoutId = setTimeout(() => {
      reject(new Error('图片加载超时'));
    }, timeout);

    // 如果已经是base64或blob格式，直接返回
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      clearTimeout(timeoutId);
      resolve(url);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous'; // 处理跨域

    img.onload = () => {
      clearTimeout(timeoutId);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法创建Canvas上下文'));
          return;
        }

        ctx.drawImage(img, 0, 0);
        // 默认转为PNG格式，保持高质量和透明度
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (error: unknown) {
        reject(new Error(`转换图片失败: ${error instanceof Error ? error.message : '未知错误'}`));
      }
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error('图片加载失败'));
    };

    img.src = url;
  });
};

/**
 * 生成分享图
 *
 * @param msgIds 需要保留的消息卡片 id
 * @returns 生成图片的 dataUrl
 */
export const takeScreenShot = async (msgIds: string[], width?: number, shareQrcodeUrl?: string) => {
  /* 深拷贝当前 dom 并处理成目标样式 */
  const target = document.querySelector('#screenshot-container')!;
  const cloned = target.cloneNode(true) as HTMLElement;
  // 处理克隆元素
  const imgEle = cloned.querySelector('#screenshot-footer-id') as HTMLImageElement;

  if (imgEle) {
    if (shareQrcodeUrl) {
      try {
        imgEle.crossOrigin = 'anonymous'; // 先设置crossOrigin
        const base64Image = await convertUrlToBase64(shareQrcodeUrl);
        imgEle.src = base64Image; // 设置转换后的base64图片
      } catch (error) {
        console.error('二维码图片转换失败:', error);
        imgEle.remove(); // 转换失败时移除图片元素
      }
    } else {
      imgEle.remove();
    }
  }

  const processed = processForShare(cloned, msgIds);
  // 在这里添加跨域图片处理
  handleCrossOriginImages(processed);

  // 创建一个不可见的容器，但不添加到DOM中
  const wrapper = document.createElement('div');
  if (width) {
    wrapper.style.width = `${width}px`;
  }
  wrapper.style.position = 'absolute';
  wrapper.style.left = '-9999px';
  wrapper.style.top = '0';

  wrapper.appendChild(processed);
  document.body.appendChild(wrapper);

  // 预加载图片并生成截图
  try {
    await preLoadImagesFromElement(processed);
    const dataUrl = await elementToImg(processed);
    console.log('dataUrl', dataUrl);
    return dataUrl;
  } finally {
    // 确保总是移除临时DOM元素
    // document.body.removeChild(wrapper);
  }
};
